import {
    addWeeks,
    differenceInCalendarDays,
    differenceInCalendarWeeks,
    format,
    isSameDay,
} from 'date-fns'
import { normalizeOptimizerAvailability } from '../../../Scheduling2025/scheduleOptimizer'
import {
    getSuggestionObject,
    getSuggestionTeacherId,
} from '../magicSchedulerUtils'

const SUNDAY_TO_OPTIMIZER_DAY_INDEX = [6, 0, 1, 2, 3, 4, 5]

export const CALENDAR_START_MINUTES = 9 * 60
export const CALENDAR_END_MINUTES = 21 * 60

const getAvailabilityBlocksForDate = (
    availability,
    date,
    rangeStart = CALENDAR_START_MINUTES,
    rangeEnd = CALENDAR_END_MINUTES
) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return []
    const normalized = normalizeOptimizerAvailability(availability)
    if (!normalized.restricted) return []
    const optimizerDayIndex = SUNDAY_TO_OPTIMIZER_DAY_INDEX[date.getDay()]
    return (normalized.byDay?.[optimizerDayIndex] || [])
        .map((block) => ({
            start: Math.max(rangeStart, block.start),
            end: Math.min(rangeEnd, block.end),
        }))
        .filter((block) => block.end > block.start)
}

export const getFamilyAvailabilityBlocksForDate = getAvailabilityBlocksForDate

export const getTeacherAvailabilityBlocksForDate = getAvailabilityBlocksForDate

export const formatMinutes = (minutes) => {
    const date = new Date(2000, 0, 1, 0, Number(minutes) || 0)
    return format(date, 'h:mm a')
}

export const getCalendarPosition = (
    minutes,
    rangeStart = CALENDAR_START_MINUTES,
    rangeEnd = CALENDAR_END_MINUTES
) => (
    ((Math.min(rangeEnd, Math.max(rangeStart, minutes)) - rangeStart)
        / (rangeEnd - rangeStart)) * 100
)

export const getCalendarMinutesFromPointer = (
    pointerOffset,
    calendarHeight,
    snapMinutes = 15,
    rangeStart = CALENDAR_START_MINUTES,
    rangeEnd = CALENDAR_END_MINUTES
) => {
    const height = Number(calendarHeight)
    const offset = Number(pointerOffset)
    const interval = Math.max(1, Number(snapMinutes) || 15)
    if (!Number.isFinite(height) || height <= 0 || !Number.isFinite(offset)) return rangeStart

    const ratio = Math.min(1, Math.max(0, offset / height))
    const rawMinutes = rangeStart + (ratio * (rangeEnd - rangeStart))
    const snappedMinutes = Math.round(rawMinutes / interval) * interval
    return Math.min(rangeEnd - interval, Math.max(rangeStart, snappedMinutes))
}

const normalize = (value) => String(value || '').trim().toLowerCase()

const getRecordIds = (record = {}) => [
    record.eventId,
    record.calendarEventId,
    record._id,
    record.id,
].map(normalize).filter(Boolean)

const getLessonIds = (record = {}) => [
    record.lessonId,
    record.lessonID,
    record.LessonID,
].map(normalize).filter(Boolean)

const getRecordTeacherId = (record = {}) => normalize(
    record.teacherId || record.teacherID || record.TeacherID
)

const getRecordFamilyId = (record = {}) => normalize(
    record.familyId || record.familyID || record.FamilyID
)

const getRecordStudentId = (record = {}) => normalize(
    record.studentId || record.studentID || record.StudentID
)

const datesHaveSameTime = (left, right) => (
    left.getHours() === right.getHours()
    && left.getMinutes() === right.getMinutes()
)

const recordsShareLessonIdentity = (event, cancellation) => {
    const eventLessonIds = getLessonIds(event)
    const cancellationLessonIds = getLessonIds(cancellation)
    if (eventLessonIds.length && cancellationLessonIds.length) {
        return eventLessonIds.some((id) => cancellationLessonIds.includes(id))
    }

    const eventIds = getRecordIds(event)
    const cancellationIds = getRecordIds(cancellation)
    if (eventIds.length && cancellationIds.length) {
        return eventIds.some((id) => cancellationIds.includes(id))
    }

    const eventStudentId = getRecordStudentId(event)
    const cancellationStudentId = getRecordStudentId(cancellation)
    if (eventStudentId && cancellationStudentId) {
        return eventStudentId === cancellationStudentId
    }

    const eventFamilyId = getRecordFamilyId(event)
    const cancellationFamilyId = getRecordFamilyId(cancellation)
    if (eventFamilyId && cancellationFamilyId) {
        return eventFamilyId === cancellationFamilyId
    }

    return Boolean(
        normalize(event.studentName) && normalize(event.studentName) === normalize(cancellation.studentName)
    )
}

export const calendarEventMatchesSuggestion = (
    event,
    suggestion,
    selectedTeacherId = ''
) => {
    if (!event || !suggestion) return false
    const suggestionObject = getSuggestionObject(suggestion)
    const suggestionTeacherId = getSuggestionTeacherId(suggestion)
    if (
        selectedTeacherId
        && suggestionTeacherId
        && suggestionTeacherId !== String(selectedTeacherId)
    ) return false

    const eventIds = getRecordIds(event)
    const suggestionIds = getRecordIds(suggestionObject)
    if (eventIds.some((eventId) => suggestionIds.includes(eventId))) return true

    const eventFamilyId = normalize(event.familyId || event.familyID || event.FamilyID)
    const suggestionFamilyId = normalize(
        suggestionObject.familyId
        || suggestionObject.familyID
        || suggestionObject.FamilyID
    )
    if (eventFamilyId && suggestionFamilyId) return eventFamilyId === suggestionFamilyId

    const eventFamilyName = normalize(event.familyName || event.FamilyName)
    const suggestionFamilyName = normalize(
        suggestionObject.familyName || suggestionObject.FamilyName
    )
    if (eventFamilyName && suggestionFamilyName) {
        return eventFamilyName === suggestionFamilyName
    }

    const eventStudentName = normalize(event.studentName || event.StudentName)
    const suggestionStudentName = normalize(
        suggestionObject.studentName
        || suggestionObject.StudentName
        || suggestionObject.name
    )
    return Boolean(eventStudentName && suggestionStudentName && (
        suggestionStudentName === eventStudentName
        || suggestionStudentName.includes(eventStudentName)
        || eventStudentName.includes(suggestionStudentName)
    ))
}

const formatSuggestionDriveTime = (suggestion) => {
    const duration = String(suggestion?.duration || '').trim()
    if (duration) return duration
    const minutes = Number(suggestion?.sortingDuration)
    return Number.isFinite(minutes) ? `${Math.round(minutes)} min` : null
}

export const getCalendarEventDriveTime = (
    event,
    suggestions = [],
    selectedTeacherId = ''
) => {
    if (!event) return null
    const eventIds = getRecordIds(event)
    const eventFamilyId = normalize(
        event.familyId || event.familyID || event.FamilyID
    )
    const eventFamilyName = normalize(event.familyName || event.FamilyName)
    const eventStudentName = normalize(event.studentName || event.StudentName)

    const candidates = (Array.isArray(suggestions) ? suggestions : []).filter(
        (suggestion) => {
            const suggestionObject = getSuggestionObject(suggestion)
            if (normalize(suggestionObject.type) === 'teacher') return false
            const suggestionTeacherId = getSuggestionTeacherId(suggestion)
            return !selectedTeacherId
                || !suggestionTeacherId
                || suggestionTeacherId === String(selectedTeacherId)
        }
    )

    const directMatch = candidates.find((suggestion) => {
        const suggestionIds = getRecordIds(getSuggestionObject(suggestion))
        return eventIds.some((eventId) => suggestionIds.includes(eventId))
    })
    if (directMatch) return formatSuggestionDriveTime(directMatch)

    if (eventFamilyId) {
        const familyMatch = candidates.find((suggestion) => {
            const suggestionObject = getSuggestionObject(suggestion)
            return normalize(
                suggestionObject.familyId
                || suggestionObject.familyID
                || suggestionObject.FamilyID
            ) === eventFamilyId
        })
        if (familyMatch) return formatSuggestionDriveTime(familyMatch)
    }

    const nameMatch = candidates.find((suggestion) => {
        const suggestionObject = getSuggestionObject(suggestion)
        const suggestionFamilyName = normalize(
            suggestionObject.familyName || suggestionObject.FamilyName
        )
        const suggestionStudentName = normalize(
            suggestionObject.studentName
            || suggestionObject.StudentName
            || suggestionObject.name
        )
        return (eventFamilyName && suggestionFamilyName === eventFamilyName)
            || (eventStudentName && suggestionStudentName && (
                suggestionStudentName === eventStudentName
                || suggestionStudentName.includes(eventStudentName)
                || eventStudentName.includes(suggestionStudentName)
            ))
    })

    return nameMatch ? formatSuggestionDriveTime(nameMatch) : null
}

const isEveryOtherWeekEvent = (event) => (
    event?.recurring?.isOn === true
    && normalize(event?.recurring?.frequency).replace(/\s+/g, '-') === 'every-other-week'
)

const createOccurrenceDate = (date, originalDate) => {
    const occurrence = new Date(date)
    occurrence.setHours(
        originalDate.getHours(),
        originalDate.getMinutes(),
        originalDate.getSeconds(),
        originalDate.getMilliseconds()
    )
    return occurrence
}

export const getEveryOtherWeekGhostEventsForDate = (events = [], date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return []

    return (Array.isArray(events) ? events : []).reduce((ghosts, event) => {
        if (!isEveryOtherWeekEvent(event)) return ghosts
        const originalDate = new Date(event.dateTime)
        if (Number.isNaN(originalDate.getTime()) || originalDate.getDay() !== date.getDay()) {
            return ghosts
        }

        const ghostDate = createOccurrenceDate(date, originalDate)
        const displayedWeekDifference = differenceInCalendarWeeks(
            ghostDate,
            originalDate,
            { weekStartsOn: 0 }
        )
        if (displayedWeekDifference % 2 === 0) return ghosts

        const nextOccurrence = addWeeks(ghostDate, 1)
        const nextWeekDifference = differenceInCalendarWeeks(
            nextOccurrence,
            originalDate,
            { weekStartsOn: 0 }
        )
        if (nextWeekDifference < 0 || nextWeekDifference % 2 !== 0) return ghosts

        const recurringEndDate = event.recurringEndDate
            ? new Date(event.recurringEndDate)
            : null
        if (
            recurringEndDate
            && !Number.isNaN(recurringEndDate.getTime())
            && nextOccurrence >= recurringEndDate
        ) return ghosts

        const nextWeekIsSkipped = (Array.isArray(event.recurringExceptions)
            ? event.recurringExceptions
            : []).some((exception) => {
            const exceptionDate = new Date(exception)
            return !Number.isNaN(exceptionDate.getTime())
                && isSameDay(exceptionDate, nextOccurrence)
        })
        if (nextWeekIsSkipped) return ghosts

        ghosts.push({
            ...event,
            dateTime: ghostDate.toISOString(),
            isEveryOtherWeekGhost: true,
            nextOccurrenceDate: nextOccurrence.toISOString(),
        })
        return ghosts
    }, [])
}

const getRecurringIntervalWeeks = (event) => {
    if (event?.recurring?.isOn !== true) return 0
    const frequency = normalize(event?.recurring?.frequency).replace(/\s+/g, '-')
    if (frequency === 'weekly') return 1
    if (frequency === 'every-other-week') return 2
    return 0
}

const recurringSeriesContainsOccurrence = (event, occurrenceDate) => {
    const originalDate = new Date(event?.dateTime)
    const intervalWeeks = getRecurringIntervalWeeks(event)
    if (
        !intervalWeeks
        || Number.isNaN(originalDate.getTime())
        || occurrenceDate.getDay() !== originalDate.getDay()
        || !datesHaveSameTime(occurrenceDate, originalDate)
    ) return false

    const weekDifference = differenceInCalendarWeeks(
        occurrenceDate,
        originalDate,
        { weekStartsOn: 0 }
    )
    return weekDifference >= 0 && weekDifference % intervalWeeks === 0
}

const recurringSeriesContinuesAfter = (event, occurrenceDate) => {
    const intervalWeeks = getRecurringIntervalWeeks(event)
    if (!intervalWeeks) return false
    const nextOccurrence = addWeeks(occurrenceDate, intervalWeeks)
    const recurringEndDate = event?.recurringEndDate
        ? new Date(event.recurringEndDate)
        : null
    return !recurringEndDate
        || Number.isNaN(recurringEndDate.getTime())
        || nextOccurrence < recurringEndDate
}

const hasMatchingRecurringException = (event, occurrenceDate) => (
    (Array.isArray(event?.recurringExceptions) ? event.recurringExceptions : [])
        .some((exception) => {
            const exceptionDate = new Date(exception)
            return !Number.isNaN(exceptionDate.getTime())
                && isSameDay(exceptionDate, occurrenceDate)
        })
)

export const getRecurringExceptionGhostEventsForDate = (
    events = [],
    cancellations = [],
    date
) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return []

    const recurringEvents = (Array.isArray(events) ? events : []).filter(
        (event) => getRecurringIntervalWeeks(event) > 0
    )
    const seen = new Set()

    return recurringEvents.reduce((ghosts, event) => {
        const originalDate = new Date(event?.dateTime)
        if (Number.isNaN(originalDate.getTime())) return ghosts
        const occurrenceDate = createOccurrenceDate(date, originalDate)
        if (
            !recurringSeriesContainsOccurrence(event, occurrenceDate)
            || !hasMatchingRecurringException(event, occurrenceDate)
            || !recurringSeriesContinuesAfter(event, occurrenceDate)
        ) return ghosts

        const cancellation = (Array.isArray(cancellations) ? cancellations : []).find(
            (candidate) => {
                const cancellationDate = new Date(candidate?.dateTime)
                const eventTeacherId = getRecordTeacherId(event)
                const cancellationTeacherId = getRecordTeacherId(candidate)
                if (
                    Number.isNaN(cancellationDate.getTime())
                    || !isSameDay(cancellationDate, occurrenceDate)
                    || !datesHaveSameTime(cancellationDate, occurrenceDate)
                    || (
                        eventTeacherId
                        && cancellationTeacherId
                        && eventTeacherId !== cancellationTeacherId
                    )
                ) return false
                return recordsShareLessonIdentity(event, candidate)
            }
        ) || null
        const movedEvent = (Array.isArray(events) ? events : []).find((candidate) => {
            if (candidate?.recurring?.isOn === true) return false
            const movedDate = new Date(candidate?.dateTime)
            const eventTeacherId = getRecordTeacherId(event)
            const movedTeacherId = getRecordTeacherId(candidate)
            return !Number.isNaN(movedDate.getTime())
                && Math.abs(differenceInCalendarDays(movedDate, occurrenceDate)) <= 6
                && (!eventTeacherId || !movedTeacherId || eventTeacherId === movedTeacherId)
                && recordsShareLessonIdentity(event, candidate)
        }) || null

        const occurrenceKey = normalize(cancellation?.occurrenceKey) || [
            getRecordTeacherId(event),
            getLessonIds(event)[0] || getRecordIds(event)[0],
            format(occurrenceDate, 'yyyy-MM-dd'),
        ].join('|')
        if (seen.has(occurrenceKey)) return ghosts
        seen.add(occurrenceKey)

        const intervalWeeks = getRecurringIntervalWeeks(event)
        ghosts.push({
            ...event,
            dateTime: occurrenceDate.toISOString(),
            isRecurringExceptionGhost: true,
            isCancelledRecurringGhost: Boolean(cancellation),
            exceptionType: cancellation
                ? 'cancellation'
                : movedEvent
                    ? 'moved'
                    : 'skipped',
            cancelledBy: cancellation
                ? cancellation.teacherCancelled === true ? 'teacher' : 'family'
                : '',
            cancellationId: cancellation?._id || cancellation?.id || '',
            cancellationOccurrenceKey: occurrenceKey,
            movedToDateTime: movedEvent?.dateTime || '',
            nextOccurrenceDate: addWeeks(occurrenceDate, intervalWeeks).toISOString(),
        })
        return ghosts
    }, [])
}

export const getCancelledRecurringGhostEventsForDate = (
    events = [],
    cancellations = [],
    date
) => getRecurringExceptionGhostEventsForDate(events, cancellations, date)
    .filter((event) => event.isCancelledRecurringGhost)

export const getRecurringExceptionConflictForEvent = (
    proposedEvent,
    events = [],
    cancellations = []
) => {
    const proposedStart = new Date(proposedEvent?.dateTime)
    if (Number.isNaN(proposedStart.getTime())) return null
    const proposedEnd = new Date(
        proposedStart.getTime()
        + (Math.max(15, Number(proposedEvent?.lessonLength) || 30) * 60 * 1000)
    )
    const proposedTeacherId = getRecordTeacherId(proposedEvent)

    return getRecurringExceptionGhostEventsForDate(
        events,
        cancellations,
        proposedStart
    ).find((reservedEvent) => {
        const reservedTeacherId = getRecordTeacherId(reservedEvent)
        if (
            proposedTeacherId
            && reservedTeacherId
            && proposedTeacherId !== reservedTeacherId
        ) return false

        const reservedStart = new Date(reservedEvent.dateTime)
        const reservedEnd = new Date(
            reservedStart.getTime()
            + (Math.max(15, Number(reservedEvent.lessonLength) || 30) * 60 * 1000)
        )
        return proposedStart < reservedEnd && proposedEnd > reservedStart
    }) || null
}

export const getCancelledRecurringConflictForEvent = (
    proposedEvent,
    events = [],
    cancellations = []
) => {
    const conflict = getRecurringExceptionConflictForEvent(
        proposedEvent,
        events,
        cancellations
    )
    return conflict?.isCancelledRecurringGhost ? conflict : null
}
